import React, { useContext, useState, useRef, useLayoutEffect, useEffect, useMemo } from "react";
import {
  Menu,
  Row,
  Col,
  Typography,
  theme,
  Image,
  Tooltip,
  Divider,
  Button,
  Modal,
  Dropdown,
} from "antd";
import { CalendarOutlined, LeftOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { getuserRoleTabs } from "./getuserRoleTabs";
import { listClinicalFormsForAdmissionApi } from "../../services/clinicalForms.services";
import {
  admissionQuery,
  detectAdmissionType,
} from "./ClinicalPractitioner/admissionContext";
import { sortByIdAsc } from "../../hooks/useClinicalRoleForms";
import bedImg from "../../assets/images/patientIcons/bed.svg";
// import { isDesktop } from "react-device-detect";
import { UserContext } from "../../context/UserContext";
// import { useParams } from "react-router-dom";
import { getCookie } from "../../helpers/localStorage";
import { Role, FacilityID } from "../../constants/defaultKeys";
import { getRoleDashboardPath } from "../../helpers/auth/helper";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import { UiTag } from "../ui";
import { formatPatientAge } from "../../helpers/formatAge";
import { measureCompactPatientHeaderBottom } from "../../helpers/utility";
import useHorizontalScrollAffordance from "../../hooks/useHorizontalScrollAffordance";
import useTabHelperLabel, { resolveTabAnchor } from "../../hooks/useTabHelperLabel";
import { useLocation } from "react-router-dom";
import { resolveReturnTo } from "../../helpers/urlFilters";
import "../PatientsOP/patientHeader.css";

const { useToken } = theme;

const COMPACT_TABS_MEDIA = "(max-width: 768px)";
const TABLET_TABS_MEDIA = "(min-width: 769px) and (max-width: 1280px)";

const TAB_TITLE_FALLBACKS = {
  billing: "Billing",
  services: "Services",
  packages: "Packages",
  "bed-management": "Bed Management",
  landing: "Summary",
  ia: "Initial Assessment",
  review: "Review",
  "progress-scales": "Progress Scales",
  risk_assessment: "Risk Assessment",
  "physio-care": "Physio Care",
  medicine: "Medicine",
  investigations: "Investigations",
  "order-sheet": "Care Items",
  diet: "Diet",
  progress_monitoring: "Progress Notes",
  chat: "Chat",
  monitoring: "Vital Monitoring",
  gallery: "Gallery",
  "monitoring-charts": "Monitoring Charts",
  forms: "Forms",
  emr: "Other EMRs",
  logs: "Logs",
};

const CompactTabItem = ({ tab, tabTitle, isActive, onShowLabel, onSelect }) => (
  <div className="mobile-tab-item-wrap">
    <div
      className={`mobile-tab-item ${isActive ? "active" : ""}`}
      onClick={(e) => {
        onShowLabel(tab.key, tabTitle, e.currentTarget);
        onSelect(tab.key);
      }}
    >
      {tab.label}
    </div>
  </div>
);

const PatientMenu = ({
  current,
  onClick,
  userRole,
  params,
  navigate,
  patientData,
}) => {
  // const query = useParams();
  // const params = new URLSearchParams(document?.location?.search);
  const location = useLocation();
  const capitalize = (text = "") =>
    text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const { userDetails } = useContext(UserContext);
  const details = patientData?.admission;
  const { token } = useToken();
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const role = defaultRole.toLowerCase().replaceAll("_", "-");

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );
  const [isCompactTabs, setIsCompactTabs] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(COMPACT_TABS_MEDIA).matches
      : false
  );
  const [isTabletTabs, setIsTabletTabs] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(TABLET_TABS_MEDIA).matches
      : false
  );
  const [tabsOpen, setTabsOpen] = useState(true);
  const [infoOpen, setInfoOpen] = useState(false);
  const [menuOpenKeys, setMenuOpenKeys] = useState([]);
  // Controlled Forms/Other dropdown (tablet + compact) — closes on select
  const [openSubmenuKey, setOpenSubmenuKey] = useState(null);
  // Prevent onOpenChange from reopening Forms/Other after a leaf item is clicked
  const forceCloseMenuRef = useRef(false);
  const selectedTab = params?.type || current;
  const helperLabelsEnabled = isCompactTabs || isTabletTabs;
  const { showTabLabel, dismissTabLabel, TabHelperLabel } = useTabHelperLabel(
    helperLabelsEnabled
  );

  const [clinicalForms, setClinicalForms] = useState([]);
  const [clinicalFormsLoaded, setClinicalFormsLoaded] = useState(false);
  useEffect(() => {
    if (!params?.patient) {
      setClinicalForms([]);
      setClinicalFormsLoaded(false);
      return;
    }
    const admissionType = detectAdmissionType(
      window?.location?.pathname,
      window?.location?.search
    );
    let cancelled = false;
    setClinicalFormsLoaded(false);
    listClinicalFormsForAdmissionApi(
      admissionQuery(admissionType, params.patient)
    )
      .then((res) => {
        if (cancelled) return;
        setClinicalForms(sortByIdAsc(res?.data));
        setClinicalFormsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setClinicalForms([]);
        setClinicalFormsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userRole, params?.patient]);

  /* `for-admission` answers with every role's forms for the admission, not
     just the signed-in practitioner's - a psychologist was being offered the
     doctor's, physiotherapist's and nutritionist's forms under Forms. A
     clinical practitioner is left alone: their forms are keyed by
     `clinical_role` rather than by this one. */
  const roleScopedForms = useMemo(() => {
    if (userRole === "Clinical_Practitioner") return clinicalForms;
    return (clinicalForms || []).filter((form) => form?.role === userRole);
  }, [clinicalForms, userRole]);

  const rawTabItems = getuserRoleTabs(userRole, selectedTab, token, {
    hideTooltips: isCompactTabs || isTabletTabs,
    compactIcons: isCompactTabs || isTabletTabs,
    clinicalForms: roleScopedForms,
  });
  // A practitioner can have dozens of clinical forms, so the Forms submenu
  // would otherwise run past the bottom of the window with nothing to scroll.
  const tabItems = rawTabItems.map((item) =>
    item.children?.length
      ? { ...item, popupClassName: "patient-menu-submenu-popup" }
      : item
  );
  const tabItemsWithSubmenu = tabItems.filter((item) => item.children?.length);
  const tabItemsMain = tabItems.filter((item) => !item.children?.length);
  const firstTabKey = tabItemsMain[0]?.key;

  useEffect(() => {
    if (userRole !== "Clinical_Practitioner") return;
    if (!params?.patient) return;
    if (!clinicalFormsLoaded) return;
    if (!firstTabKey) return;
    const validKeys = new Set([
      ...tabItemsMain.map((t) => t.key),
      ...tabItemsWithSubmenu.flatMap((t) =>
        (t.children || []).map((c) => c.key)
      ),
    ]);
    if (params?.type && validKeys.has(params.type)) return;
    onClick({ key: firstTabKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userRole,
    params?.patient,
    params?.type,
    clinicalFormsLoaded,
    firstTabKey,
  ]);

  const isSubmenuTabActive = (tab) =>
    (tab.children || []).some((child) => child.key === selectedTab);

  const getTabTitle = (key) => {
    const item = tabItems.find((tab) => tab.key === key);
    if (item?.tabTitle || item?.title) return item.tabTitle || item.title;
    for (const tab of tabItems) {
      const child = (tab.children || []).find((c) => c.key === key);
      if (child) {
        return (
          child.tabTitle ||
          (typeof child.label === "string" ? child.label : null) ||
          TAB_TITLE_FALLBACKS[key] ||
          key
        );
      }
    }
    return TAB_TITLE_FALLBACKS[key] || key;
  };

  const isSubmenuChildKey = (key) =>
    tabItems.some((tab) =>
      (tab.children || []).some((child) => child.key === key)
    );

  const revealTabLabel = (key, domEventOrAnchor) => {
    if (!helperLabelsEnabled) return;
    // Forms / Other EMRs children: never show floating helper over the dropdown
    if (isSubmenuChildKey(key)) {
      dismissTabLabel();
      return;
    }
    const title = getTabTitle(key);
    const anchor = resolveTabAnchor(domEventOrAnchor);
    if (!title || !anchor) return;
    window.requestAnimationFrame(() => {
      showTabLabel(key, title, anchor);
    });
  };

  const closeSubmenus = () => {
    setOpenSubmenuKey(null);
    forceCloseMenuRef.current = true;
    setMenuOpenKeys([]);
    window.setTimeout(() => {
      forceCloseMenuRef.current = false;
    }, 300);
  };

  const handleMenuClick = (info) => {
    if (helperLabelsEnabled && !isCompactTabs) {
      if (isSubmenuChildKey(info.key)) {
        dismissTabLabel();
      } else {
        revealTabLabel(info.key, info.domEvent);
      }
    }
    // Always collapse submenus after any item click (Forms stays open otherwise
    // because Ant Design onOpenChange can re-set openKeys in the same tick).
    closeSubmenus();
    onClick(info);
  };

  const handleMenuOpenChange = (keys) => {
    // Opening/closing Forms (or any submenu) should clear any stuck helper label
    dismissTabLabel();
    if (forceCloseMenuRef.current) {
      setMenuOpenKeys([]);
      return;
    }
    setMenuOpenKeys(keys);
  };

  const handleMenuPointerUp = (e) => {
    if (!helperLabelsEnabled || isCompactTabs) return;
    const menuItem = e.target.closest?.(".ant-menu-item");
    if (!menuItem) return;
    const menuRoot = menuItem.closest(".ant-menu");
    const menuItems = menuRoot?.querySelectorAll(".ant-menu-item");
    if (!menuItems?.length) return;
    const index = Array.from(menuItems).indexOf(menuItem);
    const visibleTabs = isTabletTabs ? tabItemsMain : tabItems;
    const tab = visibleTabs[index];
    if (!tab) return;
    revealTabLabel(tab.key, menuItem);
  };

  const handleCompactTabSelect = (key) => {
    setOpenSubmenuKey(null);
    onClick({ key });
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(TABLET_TABS_MEDIA);
    const onChange = () => setIsTabletTabs(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_TABS_MEDIA);
    const onChange = () => setIsCompactTabs(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const headerRef = useRef(null);
  const tabsScrollRef = useRef(null);

  const handleSubmenuDropdownClick = (key) => {
    closeSubmenus();
    dismissTabLabel();
    onClick({ key });
    // After selecting a Forms/Other child, bring the Forms trigger back into view
    // so horizontal tab scrolling remains usable on mobile/tablet.
    requestAnimationFrame(() => {
      const scrollEl = tabsScrollRef.current;
      if (!scrollEl) return;
      const activeTrigger =
        scrollEl.querySelector(".mobile-tab-item.active") ||
        scrollEl.querySelector(".patient-menu-submenu-trigger.active");
      activeTrigger?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    });
  };

  useHorizontalScrollAffordance(tabsScrollRef, [
    isCompactTabs,
    isTabletTabs,
    tabsOpen,
    tabItems.length,
    tabItemsWithSubmenu.length,
    params?.type,
    openSubmenuKey,
  ]);

  // Forms / Other EMRs dropdown: close when user scrolls the header or tab strip
  useEffect(() => {
    const submenuOpen = Boolean(openSubmenuKey) || menuOpenKeys.length > 0;
    if (!submenuOpen) return undefined;

    const onHeaderScroll = (e) => {
      // Don't close while scrolling the dropdown menu itself
      if (e.target?.closest?.(".ant-dropdown, .ant-dropdown-menu")) return;
      closeSubmenus();
      dismissTabLabel();
    };

    const scrollTargets = [tabsScrollRef.current, headerRef.current].filter(
      Boolean
    );
    scrollTargets.forEach((el) => {
      el.addEventListener("scroll", onHeaderScroll, { passive: true });
      el.addEventListener("wheel", onHeaderScroll, { passive: true });
      el.addEventListener("touchmove", onHeaderScroll, { passive: true });
    });

    return () => {
      scrollTargets.forEach((el) => {
        el.removeEventListener("scroll", onHeaderScroll);
        el.removeEventListener("wheel", onHeaderScroll);
        el.removeEventListener("touchmove", onHeaderScroll);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSubmenuKey, menuOpenKeys]);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const apply = () => {
      const bottom = measureCompactPatientHeaderBottom(el);
      const px = `${bottom}px`;
      document.documentElement.style.setProperty(
        "--op-patient-header-height",
        px
      );
      document.documentElement.style.setProperty(
        "--ip-patient-header-height",
        px
      );
    };
    apply();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", apply);
      return () => {
        window.removeEventListener("resize", apply);
        document.documentElement.style.removeProperty(
          "--ip-patient-header-height"
        );
        document.documentElement.style.removeProperty(
          "--op-patient-header-height"
        );
      };
    }
    const ro = new ResizeObserver(() => apply());
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      document.documentElement.style.removeProperty(
        "--ip-patient-header-height"
      );
      document.documentElement.style.removeProperty(
        "--op-patient-header-height"
      );
    };
  }, [isCompactTabs, isTabletTabs, tabsOpen, userRole, details, params?.type]);

  useEffect(() => {
    const collapsed = isCompactTabs && !tabsOpen;
    document.documentElement.classList.toggle(
      "ip-patient-header-collapsed",
      collapsed
    );
    return () => {
      document.documentElement.classList.remove("ip-patient-header-collapsed");
    };
  }, [isCompactTabs, tabsOpen]);

  const formatBedDays = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num =
      typeof value === "number"
        ? value
        : typeof value === "string"
        ? Number.parseFloat(value)
        : Number(value);
    if (Number.isNaN(num)) return String(value);
    return String(num);
  };

  const trimValue = (value) =>
    value == null ? "" : String(value).trim();

  const consultantEntries = (details?.primary_consultant_details || [])
    .map((doc) => ({
      name: trimValue(doc?.primary_consultant_name),
      designation: trimValue(doc?.primary_consultant_designation),
      hospital: trimValue(doc?.primary_consultant_hospital),
    }))
    .filter(({ name, designation, hospital }) => name || designation || hospital);

  const navigateToHome = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("patient");
    url.searchParams.delete("type");
    url.searchParams.delete("chat");
    navigate(`${window.location.pathname}?${url.searchParams.toString()}`, {
      // replace: true,
      // This prevents adding a new entry in the history stack
    });
  };

  const handleBack = () => {
    // Prefer history state; the query param is only a fallback for links
    // created before the origin moved out of the URL.
    const returnPath =
      resolveReturnTo(location?.state?.returnTo) ||
      resolveReturnTo(params?.returnTo);
    if (returnPath) {
      navigate(returnPath);
      return;
    }
    if (role === "receptionist") {
      navigate(-1);
      return;
    }
    navigateToHome();
  };
  // console.log(query?.tab);
  const patientFullName = `${capitalize(details?.patient?.first_name || "")} ${
    details?.patient?.last_name || ""
  }`.trim();

  const renderInfoField = (label, value, fullWidth = false) => (
    <div
      className={`patient-info-field${
        fullWidth ? " patient-info-field--full" : ""
      }`}
    >
      <span className="patient-info-field__label">{label}</span>
      <span className="patient-info-field__value">{value || "-"}</span>
    </div>
  );

  const desktopInfoContent = (
    <div className="patient-info-popover-content">
      {consultantEntries.length > 0 ? (
        <>
          <Typography.Text style={{ fontWeight: 500, fontSize: "18px" }}>
            Consultant Details
          </Typography.Text>
          <div style={{ marginTop: 6, fontSize: 14, lineHeight: "18px" }}>
            {consultantEntries.map((doc, index) => (
              <div key={index} style={{ marginBottom: 6 }}>
                {doc.name && (
                  <span style={{ fontWeight: 600, color: "#101828" }}>
                    {doc.name}
                  </span>
                )}
                {doc.designation && (
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#344054",
                      marginLeft: doc.name ? 4 : 0,
                    }}
                  >
                    {doc.name ? `(${doc.designation})` : doc.designation}
                  </span>
                )}
                {doc.hospital && (
                  <>
                    {(doc.name || doc.designation) && (
                      <span style={{ margin: "0 4px", color: "#667085" }}>
                        {" "}
                        -{" "}
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#667085",
                      }}
                    >
                      {doc.hospital}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
          <Divider style={{ margin: "10px 0" }} />
        </>
      ) : null}

      <Typography.Text style={{ fontWeight: 500, fontSize: "18px" }}>
        Patient Details
      </Typography.Text>
      <div className="patient-info-detail-row patient-info-detail-row--split">
        <div>
          <div style={{ fontSize: 12, color: "#98A2B3" }}>Patient Name</div>
          <div>{patientFullName || "-"}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#98A2B3" }}>IP ID</div>
          <div>{details?.ip_id || "-"}</div>
        </div>
      </div>
      <div style={{ lineHeight: "22px" }}>
        <div className="patient-info-detail-row patient-info-detail-row--split">
          <div>
            <div style={{ fontSize: 12, color: "#98A2B3" }}>Contact Number</div>
            <div>{details?.patient?.mobile || "-"}</div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#98A2B3" }}>Relation</div>
            <div>{details?.emergency_contact_relation || "-"}</div>
          </div>
        </div>

        <div className="patient-info-detail-row patient-info-detail-row--split">
          <div>
            <div style={{ fontSize: 12, color: "#98A2B3" }}>
              Emergency Contact Name
            </div>
            <div>{details?.emergency_contact_name}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#98A2B3" }}>Blood Group</div>
            <div>{details?.patient?.blood_group || "-"}</div>
          </div>
        </div>

        <div className="patient-info-detail-row">
          <div style={{ fontSize: 12, color: "#98A2B3" }}>Address</div>
          <div>{details?.patient?.address}</div>
        </div>

        <div className="patient-info-detail-row">
          <div style={{ fontSize: 12, color: "#98A2B3" }}>Message</div>
          <div>{details?.message || "-"}</div>
        </div>
      </div>
    </div>
  );

  const mobileInfoContent = (
    <div className="patient-info-popover-content patient-info-mobile">
      <div className="patient-info-mobile__chips">
        {(details?.patient?.age_by_dob || details?.patient?.gender) && (
          <div className="patient-info-chip">
            <span className="patient-info-chip__label">Age / Gender</span>
            <span className="patient-info-chip__value">
              {formatPatientAge(
                details?.patient?.age_by_dob,
                details?.patient?.gender
              )}
            </span>
          </div>
        )}
        {details?.admission_date && (
          <div className="patient-info-chip">
            <span className="patient-info-chip__label">DOA</span>
            <span className="patient-info-chip__value">
              {dayjs(details?.admission_date).format("DD-MM-YYYY")}
            </span>
          </div>
        )}
        {details?.total_bed_days > 0 && (
          <div className="patient-info-chip">
            <span className="patient-info-chip__label">Bed Days</span>
            <span className="patient-info-chip__value">
              {formatBedDays(details?.total_bed_days)}
            </span>
          </div>
        )}
      </div>

      {consultantEntries.length > 0 ? (
        <section className="patient-info-mobile__section">
          <h4 className="patient-info-mobile__heading">Consultant</h4>
          <div className="patient-info-mobile__consultant-list">
            {consultantEntries.map((doc, index) => (
              <div key={index} className="patient-info-mobile__consultant">
                {(doc.name || doc.designation) && (
                  <div className="patient-info-mobile__consultant-name">
                    {doc.name}
                    {doc.designation ? ` (${doc.designation})` : ""}
                  </div>
                )}
                {doc.hospital && (
                  <div className="patient-info-mobile__consultant-hospital">
                    {doc.hospital}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="patient-info-mobile__section">
        <h4 className="patient-info-mobile__heading">Patient Details</h4>
        <div className="patient-info-mobile__grid">
          {renderInfoField("Name", patientFullName)}
          {renderInfoField("IP ID", details?.ip_id)}
          {renderInfoField("Contact", details?.patient?.mobile)}
          {renderInfoField("Relation", details?.emergency_contact_relation)}
          {renderInfoField(
            "Emergency Contact",
            details?.emergency_contact_name
          )}
          {renderInfoField("Blood Group", details?.patient?.blood_group)}
          {renderInfoField("Address", details?.patient?.address, true)}
          {details?.message
            ? renderInfoField("Message", details?.message, true)
            : null}
        </div>
      </section>
    </div>
  );

  const infoContent = isMobile ? mobileInfoContent : desktopInfoContent;

  return (
    <>
    <Row
      ref={headerRef}
      className={`patient-menu-wrapper ${
        isTabletTabs && !isCompactTabs
          ? "patient-menu-wrapper--tablet-tabs"
          : ""
      } ${
        isCompactTabs ? "patient-menu-wrapper--compact-tabs" : ""
      } ${
        isCompactTabs && !tabsOpen
          ? "patient-menu-wrapper--tabs-collapsed"
          : ""
      }`}
      gutter={
        isCompactTabs
          ? [12, 0]
          : isTabletTabs && !isCompactTabs
          ? [0, 6]
          : [12, 12]
      }
      style={{
        position: "fixed",
        top: "0",
        zIndex: "1000",
        backgroundColor: "white",
        padding: "6px 16px 2px",
        boxShadow: "0 2px 4px -4px #222",
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
      }}
    >
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={9}
        xl={8}
        xxl={6}
        flex={
          isTabletTabs && !isCompactTabs ? "0 0 auto" : undefined
        }
        className="patient-menu-info-col"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            marginLeft: "8px",
            marginTop: "4px",
          }}
        >
          <div className="patient-header-name-row">
            <LeftOutlined
              onClick={handleBack}
              style={{
                fontSize: "18px",
                padding: "0px 6px",
                marginTop: "6px",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />

            <div className="patient-header-name-bed-group">
              {isMobile || isCompactTabs ? (
                <Typography.Text
                  className="patient-header-name"
                  onClick={(e) => {
                    const title = patientFullName?.trim();
                    if (title) {
                      showTabLabel("patient-name", title, e.currentTarget);
                    }
                  }}
                  style={{
                    color: "#101828",
                    fontSize: "16px",
                    fontWeight: 600,
                    whiteSpace: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    lineHeight: 1.25,
                    minWidth: 0,
                    cursor: "pointer",
                  }}
                >
                  {patientFullName}
                </Typography.Text>
              ) : (
                <Tooltip title={patientFullName}>
                  <Typography.Text
                    className="patient-header-name"
                    style={{
                      color: "#101828",
                      fontSize: "16px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                      minWidth: 0,
                    }}
                  >
                    {patientFullName}
                  </Typography.Text>
                </Tooltip>
              )}

              {details?.bed?.bed_no && (
                <UiTag
                  tone="warning"
                  size="sm"
                  className="patient-header-bed-tag"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    margin: 0,
                    flexShrink: 0,
                  }}
                >
                  <Image
                    src={bedImg}
                    preview={false}
                    style={{ width: 14, height: 14 }}
                  />
                  {details?.bed?.bed_no}
                </UiTag>
              )}
            </div>

            {isMobile ? (
              <>
                <InfoCircleOutlined
                  role="button"
                  tabIndex={0}
                  className="patient-header-info-btn"
                  onClick={() => setInfoOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setInfoOpen(true);
                    }
                  }}
                  style={{
                    color: token.colorPrimary,
                    fontSize: "15px",
                    marginTop: "2px",
                  }}
                />
                <Modal
                  open={infoOpen}
                  onCancel={() => setInfoOpen(false)}
                  footer={null}
                  centered
                  width="min(360px, calc(100vw - 24px))"
                  wrapClassName="patient-info-modal-wrap"
                  destroyOnClose
                  styles={{
                    content: { height: "auto" },
                    body: {
                      padding: "8px 14px 14px",
                      overflowX: "hidden",
                      flex: "0 1 auto",
                      height: "auto",
                      maxHeight: "calc(100dvh - 120px)",
                    },
                  }}
                  title="Patient Info"
                  closable
                >
                  {infoContent}
                </Modal>
              </>
            ) : (
              <Popover
                content={
                  <div style={{ overflowY: "auto", maxHeight: "70vh" }}>
                    {desktopInfoContent}
                  </div>
                }
                trigger="click"
                open={infoOpen}
                onOpenChange={setInfoOpen}
                placement="bottomLeft"
                overlayClassName="patient-info-popover-overlay"
                destroyTooltipOnHide
              >
                <span
                  role="button"
                  tabIndex={0}
                  className="patient-header-info-btn"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setInfoOpen((open) => !open);
                    }
                  }}
                >
                  <InfoCircleOutlined
                    style={{
                      color: token.colorPrimary,
                      fontSize: "15px",
                      marginTop: "2px",
                    }}
                  />
                </span>
              </Popover>
            )}
          </div>

          {!isMobile && (
            <div className="patient-header-meta-row">
              {(details?.patient?.age_by_dob || details?.patient?.gender) && (
                <UiTag tone="neutral" size="sm">
                  {formatPatientAge(
                    details?.patient?.age_by_dob,
                    details?.patient?.gender
                  )}
                </UiTag>
              )}

              {details?.admission_date && (
                <UiTag
                  tone="neutral"
                  size="sm"
                  style={
                    role === "nurse" && params?.status === "current"
                      ? {
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          margin: 0,
                        }
                      : undefined
                  }
                >
                  {role === "nurse" && params?.status === "current" && (
                    <CalendarOutlined style={{ color: token.colorPrimary }} />
                  )}
                  DOA: {dayjs(details?.admission_date).format("DD-MM-YYYY")}
                </UiTag>
              )}
              {details?.total_bed_days > 0 && (
                <UiTag tone="info" size="sm">
                  Bed Days: {formatBedDays(details?.total_bed_days)}
                </UiTag>
              )}
            </div>
          )}

          {isCompactTabs && !tabsOpen && (
            <div className="op-patient-tabs-toggle-row ip-patient-tabs-toggle-row ip-patient-tabs-toggle-row--collapsed">
              <Button
                type="text"
                className="op-patient-tabs-toggle op-patient-tabs-toggle--bottom"
                style={{ color: token.colorPrimary }}
                aria-label="Expand tab icons"
                onClick={() => setTabsOpen(true)}
                icon={<DownOutlined />}
              />
            </div>
          )}
        </div>
      </Col>

      {(!isCompactTabs || tabsOpen) && (
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={15}
        xl={16}
        xxl={18}
        flex={isTabletTabs && !isCompactTabs ? "1 1 0" : undefined}
        className="patient-menu-tabs-col"
      >
        {isCompactTabs ? (
          <>
            {tabsOpen ? (
              <div
                ref={tabsScrollRef}
                className="mobile-tab-scroll ip-compact-patient-tabs"
              >
                {tabItemsMain.map((tab) => (
                  <CompactTabItem
                    key={tab.key}
                    tab={tab}
                    tabTitle={getTabTitle(tab.key)}
                    isActive={selectedTab === tab.key}
                    onShowLabel={showTabLabel}
                    onSelect={handleCompactTabSelect}
                  />
                ))}
                {tabItemsWithSubmenu.map((tab) => (
                  <span key={tab.key} className="patient-tab-submenu-wrap">
                    <Dropdown
                      trigger={["click"]}
                      placement="bottom"
                      open={openSubmenuKey === tab.key}
                      getPopupContainer={() => document.body}
                      onOpenChange={(open) => {
                        dismissTabLabel();
                        setOpenSubmenuKey(open ? tab.key : null);
                      }}
                      menu={{
                        selectedKeys: [selectedTab],
                        onClick: ({ key }) => handleSubmenuDropdownClick(key),
                        items: (tab.children || []).map((child) => ({
                          key: child.key,
                          label: child.label,
                        })),
                      }}
                    >
                      <div
                        className={`mobile-tab-item mobile-tab-item--text ${
                          isSubmenuTabActive(tab) ? "active" : ""
                        }`}
                      >
                        {tab.label}
                      </div>
                    </Dropdown>
                  </span>
                ))}
              </div>
            ) : null}
            <div className="op-patient-tabs-toggle-row ip-patient-tabs-toggle-row">
              <Button
                type="text"
                className="op-patient-tabs-toggle op-patient-tabs-toggle--bottom"
                style={{ color: token.colorPrimary }}
                aria-label={
                  tabsOpen ? "Collapse tab icons" : "Expand tab icons"
                }
                onClick={() => setTabsOpen((v) => !v)}
                icon={tabsOpen ? <DownOutlined /> : <UpOutlined />}
              />
            </div>
          </>
        ) : isTabletTabs ? (
          <div
            ref={tabsScrollRef}
            className="patient-menu-scroll patient-menu-scroll--with-submenus"
            onPointerUp={handleMenuPointerUp}
          >
            <Menu
              className="patientMenu"
              style={{ justifyContent: "flex-end" }}
              onClick={handleMenuClick}
              selectedKeys={[params?.type || current]}
              mode="horizontal"
              disabledOverflow
              items={tabItemsMain}
            />
            {tabItemsWithSubmenu.map((tab) => (
              <span key={tab.key} className="patient-tab-submenu-wrap">
                <Dropdown
                  trigger={["click"]}
                  placement="bottomRight"
                  overlayClassName="patient-menu-submenu-popup"
                  open={openSubmenuKey === tab.key}
                  getPopupContainer={() => document.body}
                  onOpenChange={(open) => {
                    dismissTabLabel();
                    setOpenSubmenuKey(open ? tab.key : null);
                  }}
                  menu={{
                    selectedKeys: [selectedTab],
                    onClick: ({ key }) => handleSubmenuDropdownClick(key),
                    items: (tab.children || []).map((child) => ({
                      key: child.key,
                      label: child.label,
                    })),
                  }}
                >
                  <div
                    className={`mobile-tab-item mobile-tab-item--text patient-menu-submenu-trigger ${
                      isSubmenuTabActive(tab) ? "active" : ""
                    }`}
                  >
                    {tab.label}
                  </div>
                </Dropdown>
              </span>
            ))}
          </div>
        ) : (
          <div
            ref={tabsScrollRef}
            className="patient-menu-scroll"
            onPointerUp={handleMenuPointerUp}
          >
            <Menu
              className="patientMenu"
              style={{ justifyContent: "flex-end" }}
              onClick={handleMenuClick}
              selectedKeys={[params?.type || current]}
              openKeys={menuOpenKeys}
              onOpenChange={handleMenuOpenChange}
              mode="horizontal"
              disabledOverflow
              items={tabItems}
            />
          </div>
        )}
      </Col>
      )}
    </Row>
    {TabHelperLabel}
    </>
  );
};

export default PatientMenu;
